import React from "react";
import {
  AssetType,
  StationaryMetadata,
  AssetFormData,
} from "../../../types/asset-types";
import {
  BaseAssetFormProps,
  AssetFormHeader,
  FormField,
  TextInput,
  TextArea,
  NumberInput,
  AIConversationFields,
} from "./asset-form-base";

export function StationaryAssetForm({
  data,
  onChange,
  errors,
  disabled,
}: BaseAssetFormProps) {
  const metadata = (data.metadata as Partial<StationaryMetadata>) || {};
  const hasVenue = !!data.venue;

  const handleMetadataChange = (
    field: keyof StationaryMetadata,
    value: any
  ) => {
    const newMetadata = {
      ...metadata,
      [field]: value,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  const handleAIContentChange = (aiData: any) => {
    const newMetadata = {
      ...metadata,
      ...aiData,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  return (
    <div className="space-y-6">
      <AssetFormHeader
        type={AssetType.STATIONARY}
        description="Create a stationary asset like a billboard, poster, or fixed display with specific location coordinates."
      />

      {hasVenue && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Venue Selected: {data.venue?.name}
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Location coordinates and address will be inherited from the
                selected venue.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormField
          label="Latitude"
          error={errors?.["metadata.latitude"]}
          description="GPS latitude coordinate (e.g., 40.7128) - Optional"
        >
          <NumberInput
            value={metadata.latitude}
            onChange={(value) => handleMetadataChange("latitude", value)}
            placeholder="40.7128"
            disabled={disabled}
            step={0.000001}
            min={-90}
            max={90}
          />
        </FormField>

        <FormField
          label="Longitude"
          error={errors?.["metadata.longitude"]}
          description="GPS longitude coordinate (e.g., -74.0060) - Optional"
        >
          <NumberInput
            value={metadata.longitude}
            onChange={(value) => handleMetadataChange("longitude", value)}
            placeholder="-74.0060"
            disabled={disabled}
            step={0.000001}
            min={-180}
            max={180}
          />
        </FormField>

        <FormField
          label="Elevation"
          error={errors?.["metadata.elevation"]}
          description="Height above sea level in meters (e.g., 100) - Optional"
        >
          <NumberInput
            value={metadata.elevation}
            onChange={(value) => handleMetadataChange("elevation", value)}
            placeholder="100"
            disabled={disabled}
            step={0.1}
            min={-1000}
            max={10000}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Floor"
          error={errors?.["metadata.floor"]}
          description="Floor level (e.g., Ground, 2nd, Basement)"
        >
          <TextInput
            value={metadata.floor || ""}
            onChange={(value) => handleMetadataChange("floor", value)}
            placeholder="Ground Floor"
            disabled={disabled}
          />
        </FormField>

        <FormField
          label="Level"
          error={errors?.["metadata.level"]}
          description="Specific level or section (e.g., Mezzanine, Upper)"
        >
          <TextInput
            value={metadata.level || ""}
            onChange={(value) => handleMetadataChange("level", value)}
            placeholder="Main Level"
            disabled={disabled}
          />
        </FormField>
      </div>

      <FormField
        label="Location Description"
        required
        error={errors?.["metadata.locationDescription"]}
        description="Detailed description of where the asset is located"
      >
        <TextArea
          value={metadata.locationDescription || ""}
          onChange={(value) =>
            handleMetadataChange("locationDescription", value)
          }
          placeholder="Near the main entrance, facing the street, mounted on the east wall"
          disabled={disabled}
          required
          rows={3}
        />
      </FormField>

      <FormField
        label="Access Instructions"
        error={errors?.["metadata.accessInstructions"]}
        description="Instructions for accessing or finding this asset"
      >
        <TextArea
          value={metadata.accessInstructions || ""}
          onChange={(value) =>
            handleMetadataChange("accessInstructions", value)
          }
          placeholder="Enter through main lobby, turn right past the information desk"
          disabled={disabled}
          rows={2}
        />
      </FormField>

      <AIConversationFields
        data={metadata}
        onChange={handleAIContentChange}
        errors={errors}
        disabled={disabled}
      />
    </div>
  );
}
